/**
 * avatars/blocky-avatar-accessories.js
 * Mixin quản lý tóc, nón/mũ, kính, khẩu trang, phụ kiện thân, râu, sừng, cánh, đuôi và kiểu mắt cho BlockyAvatar.
 */

class AccessoriesHolder {
  setHair(style, color = this.config.colors.hair) {
    if (this.attachmentSlots.hair) {
      this.parts.head.remove(this.attachmentSlots.hair);
      this.attachmentSlots.hair = null;
    }
    if (style) this.config.features.hairStyle = style;
    if (color) this.config.colors.hair = color;

    // Cập nhật lại texture 6 mặt của khối đầu (gáy, tai, trán, đỉnh)
    this.updateHeadTextures();

    if (!style || style === 'bald') return;

    const hair = this.itemsLib.createHair(style, this.dimensions.headSize, color || this.config.colors.hair);
    this.parts.head.add(hair);
    this.attachmentSlots.hair = hair;

    // Áp dụng trạng thái ẩn/hiện tóc 3D phù hợp với mũ hiện tại
    this.updateHairVisibilityForHat();
    this.enforceShadowPolicy();
  }

  setHat(hatType) {
    if (this.attachmentSlots.hat) {
      this.parts.head.remove(this.attachmentSlots.hat);
      this.attachmentSlots.hat = null;
    }
    this.config.features.hat = hatType;

    if (hatType && hatType !== 'none') {
      const hat = this.itemsLib.createHat(hatType, this.dimensions.headSize);
      this.parts.head.add(hat);
      this.attachmentSlots.hat = hat;
    }

    // Tự động kiểm soát hiển thị tóc 3D để không bị xuyên mũ
    this.updateHairVisibilityForHat();
    this.enforceShadowPolicy();
  }

  updateHairVisibilityForHat() {
    const hair = this.attachmentSlots.hair;
    if (!hair) return;
    const isHatOn = !!(this.config.features.hat && this.config.features.hat !== 'none');

    if (isHatOn) {
      // Khi đội nón/mũ:
      // 1. Ẩn các khối tóc 3D trên đỉnh đầu và trước trán để không bị đâm xuyên qua mũ
      if (hair.userData && hair.userData.topHairMeshes) {
        hair.userData.topHairMeshes.forEach((m) => {
          m.visible = false;
        });
      }
      // 2. Giữ lại hai dải tóc buông xuống vai và sau lưng của kiểu tóc dài thiếu nữ
      if (hair.userData && hair.userData.lowerStrandMeshes) {
        hair.userData.lowerStrandMeshes.forEach((m) => {
          m.visible = true;
        });
      }
    } else {
      // Khi không đội mũ: khôi phục hiện toàn bộ các khối tóc 3D
      if (hair.userData && hair.userData.topHairMeshes) {
        hair.userData.topHairMeshes.forEach((m) => {
          m.visible = true;
        });
      }
      if (hair.userData && hair.userData.lowerStrandMeshes) {
        hair.userData.lowerStrandMeshes.forEach((m) => {
          m.visible = true;
        });
      }
    }
  }

  setEyewear(eyewearType) {
    if (this.attachmentSlots.eyewear) {
      this.parts.head.remove(this.attachmentSlots.eyewear);
      this.attachmentSlots.eyewear = null;
    }
    if (!eyewearType || eyewearType === 'none') return;

    const eye = this.itemsLib.createEyewear(eyewearType, this.dimensions.headSize);
    this.parts.head.add(eye);
    this.attachmentSlots.eyewear = eye;
    this.enforceShadowPolicy();
  }

  setMask(maskType) {
    if (this.attachmentSlots.mask) {
      this.parts.head.remove(this.attachmentSlots.mask);
      this.attachmentSlots.mask = null;
    }
    if (!maskType || maskType === 'none') return;

    const mask = this.itemsLib.createMask(maskType, this.dimensions.headSize);
    this.parts.head.add(mask);
    this.attachmentSlots.mask = mask;
    this.enforceShadowPolicy();
  }

  setBodyAccessory(accType) {
    if (this.attachmentSlots.bodyAcc || this.attachmentSlots.bodyAccessory) {
      const existing = this.attachmentSlots.bodyAcc || this.attachmentSlots.bodyAccessory;
      if (existing.parent) existing.parent.remove(existing);
      this.attachmentSlots.bodyAcc = null;
      this.attachmentSlots.bodyAccessory = null;
    }
    if (!accType || accType === 'none') return;

    const { torsoW, torsoH, torsoD, legH, armH, armW } = this.dimensions;
    const acc = this.itemsLib.createBodyAccessory(accType, torsoW, torsoH, torsoD, {
      shirtColor: this.config.colors.shirt,
      handY: legH + torsoH - armH,
      armOffsetX: torsoW / 2 + armW / 2
    });
    // Đồ đứng dưới đất (vali, xe đẩy) gắn vào root: chạm sàn & không lắc theo thân khi đi
    if (acc.userData.anchor === 'ground') {
      this.root.add(acc);
    } else {
      this.parts.torso.add(acc);
    }
    this.attachmentSlots.bodyAcc = acc;
    this.attachmentSlots.bodyAccessory = acc;
    this.enforceShadowPolicy();
  }

  setBeard(beardType, beardColor = this.config.colors.beard || this.config.colors.hair || '#d8d8d8') {
    if (this.attachmentSlots.beard) {
      if (this.parts.head) this.parts.head.remove(this.attachmentSlots.beard);
      if (this.headMesh) this.headMesh.remove(this.attachmentSlots.beard);
      this.attachmentSlots.beard = null;
    }
    this.config.features.beard = beardType;
    if (!beardType || beardType === 'none') return;

    const beard = this.itemsLib.createBeard(beardType, this.dimensions.headSize, beardColor);
    if (this.parts.head) {
      this.parts.head.add(beard);
    } else if (this.headMesh) {
      this.headMesh.add(beard);
    }
    this.attachmentSlots.beard = beard;
    this.enforceShadowPolicy();
  }

  setHorns(hornType, hornColor = this.config.colors.horns || this.config.colors.hair, scaleFactor = null) {
    if (this.attachmentSlots.horns) {
      this.parts.head.remove(this.attachmentSlots.horns);
      this.attachmentSlots.horns = null;
    }
    this.config.features.horns = hornType;
    if (!hornType || hornType === 'none') return;

    const horns = this.itemsLib.createHorns(hornType, this.dimensions.headSize, hornColor);

    const finalScale =
      scaleFactor !== null && scaleFactor !== undefined ? scaleFactor : this.config.bodyType === 'child' ? 0.6 : 1.0;

    if (finalScale !== 1.0) {
      horns.scale.set(finalScale, finalScale, finalScale);
    }

    this.parts.head.add(horns);
    this.attachmentSlots.horns = horns;
    this.enforceShadowPolicy();
  }

  setWings(wingType, wingColor = this.config.colors.wings || this.config.colors.shirt, scaleFactor = null) {
    if (this.attachmentSlots.wings) {
      this.parts.torso.remove(this.attachmentSlots.wings);
      this.attachmentSlots.wings = null;
    }
    this.config.features.wings = wingType;
    if (!wingType || wingType === 'none') return;

    const wings = this.itemsLib.createWings(
      wingType,
      this.dimensions.torsoW,
      this.dimensions.torsoH,
      this.dimensions.torsoD,
      wingColor
    );

    const finalScale =
      scaleFactor !== null && scaleFactor !== undefined ? scaleFactor : this.config.bodyType === 'child' ? 0.6 : 1.0;

    if (finalScale !== 1.0) {
      wings.scale.set(finalScale, finalScale, finalScale);
    }

    this.parts.torso.add(wings);
    this.attachmentSlots.wings = wings;
    this.enforceShadowPolicy();
  }

  setTail(tailType, tailColor = this.config.colors.tail || this.config.colors.skin) {
    if (this.attachmentSlots.tail) {
      this.parts.torso.remove(this.attachmentSlots.tail);
      this.attachmentSlots.tail = null;
    }
    this.config.features.tail = tailType;
    if (!tailType || tailType === 'none') return;

    const tail = this.itemsLib.createTail(
      tailType,
      this.dimensions.torsoW,
      this.dimensions.torsoH,
      this.dimensions.torsoD,
      tailColor
    );
    this.parts.torso.add(tail);
    this.attachmentSlots.tail = tail;
    this.enforceShadowPolicy();
  }

  setEyeType(eyeType) {
    this.config.features.eyeType = eyeType;
    const curExpr = this.currentExpression || 'happy';
    this.currentExpression = null; // buộc vẽ lại texture
    this.setExpression(curExpr);
  }
}

export function registerAvatarAccessoriesMethods(proto) {
  Object.getOwnPropertyNames(AccessoriesHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      Object.defineProperty(proto, name, {
        value: AccessoriesHolder.prototype[name],
        writable: true,
        configurable: true,
        enumerable: false
      });
    }
  });
}
